
import TimePickerScroll from '../fields/TimePickerScroll.vue'
import { setInputFilter } from '~/fc/Helpers/DOMHelper'
import { stringToRegex } from '~/fc/Helpers/StringHelpers';
import { secondsToHHMMSS } from '~/fc/Helpers/dateFormatter'
import { getCropper } from '~/fc/cropper'
import { getMinMaxResizeValues, getSelectedResizeUnit } from '~/fc/pdfToolsHelper';
import { formatString } from '~/fc/Helpers/FormatHelpers';

export default {
    name: 'TextField',
    components: {
        ToolTip: () => import(/* webpackChunkName: "ToolTip" */ '../../ToolTip'),
        TimePickerScroll
    },
    props: ['value', 'field', 'selection'],
    data: () => ({
        options: {},
        defaultUnit: '',
        inputType: 'text',
        units: false,
        inputValue: null,
        isValid: true,
        numberTypes: ['number', 'integer', 'float']
    }),
    computed: {
        advancedOptionListResponsiveView() { return this.$store.state.store.advancedOptionListResponsiveView },
        extraButton() { return this.field.customVariables ? this.field.customVariables.extraButton : false },
        hasRightCol() { return this.units || this.extraButton },
        dataValue() { return this.value && this.value.includes('|') ? this.value.split('|')[0] : this.value },
        dataUnit() {
            return this.value && this.value.includes('|')
                ? this.value.split('|')[1]
                : (this.units ? this.defaultUnit : false)
        },
        asTime() { return secondsToHHMMSS(this.inputValue) },
        positionXID() { return this.$store.state.advancedSetting.cropSettingsIds['crop-position-x'] },
        positionYID() { return this.$store.state.advancedSetting.cropSettingsIds['crop-position-y'] },
        positionX() { return this.$store.state.advancedSetting.userData.allSelection.root[this.positionXID] },
        positionY() { return this.$store.state.advancedSetting.userData.allSelection.root[this.positionYID] },
        resizeUnitValue() { return getSelectedResizeUnit() },
        info() { return this.$store.state.info },
        merging() { return this.$store.state.advancedSetting.merging; },
        slug() {
            if (this.merging) return this.merging
            if (this.info.slug) return this.info.slug
            return this.info.source.ext + "-to-" + this.info.target.ext
        },
        delayOptionKey() { return "gif_frame_speed" },
        isPdfResizeDimension() {
            return this.field.customVariables?.is_pdf_resize_dimension || false
        },
        isInputValid() {
            let value = this.inputValue;
            let max = this.field.customVariables?.max;
            let min = this.field.customVariables?.min;

            if (this.isPdfResizeDimension) {
                const { min: resizeMin, max: resizeMax } = getMinMaxResizeValues(this.resizeUnitValue);
                max = resizeMax || max;
                min = resizeMin || min;
            }

            let regex = stringToRegex(this.field.validation_regex);

            if (this.inputType === 'number' && value !== null && value !== undefined && value !== '') {
                if (max && value > max) return false;
                if (min && value < min) return false;
            }
            
            return regex ? regex.test(value) : true;
        },
    },
    watch: {
        value(newVal, oldVal) {
            this.inputValue = newVal
        },
        inputValue(newVal, oldVal) {

            // only check input validity if the field is a PDF resize dimension inside vue watcher
            // other input validations are handled by the setInputFilter function in DOMHelper.js
            if (this.isPdfResizeDimension) {
                this.isValid = this.isInputValid;
            }

            if (this.inputType === 'number') {
                this.inputValue = Number(newVal)
                this.onInput(Number(newVal))
            } else {
                this.onInput(newVal)
            }
        },
        resizeUnitValue() {
            if (this.resizeUnitValue && this.isPdfResizeDimension) this.isValid = this.isInputValid;
        },
        isInputValid(newVal) {
            if (this.isPdfResizeDimension) {
                const dimension = this.field?.name === 'pdf_custom_resize_width' ? 'width' : 'height';
                this.$store.commit('setIsValidPDFResizeDimension', {isValid : newVal, dimension});
            }
        },
    },
    created() {
        this.inputValue = this.value
        if (this.field.name === 'crop-position-x' || this.field.name === 'crop-position-y') { this.inputValue = 0 }
    },
    mounted() {
        this.handlePageScroll()
        this.handleUnit()
        this.initializeInput()
        this.handleCropPreset()
        if (this.field.name === "gif_frame_speed") this.$bus.$emit('UPDATE_GIF_DELAY')        
    },
    beforeDestroy() {
        if (this.isPdfResizeDimension) {
            this.$store.commit('setIsValidPDFResizeDimension',  {isValid : true});
        }
    },
    methods: {
        getValidationMessage() {
            if (!this.isPdfResizeDimension) {
                return this.field.validation_error_message || this.field.helper_text
            }
            const { min, max } = getMinMaxResizeValues(this.resizeUnitValue)
            return formatString(
                this.field.validation_error_message,
                {
                    min: min,
                    max: max,
                }
            )
        },
        handleTimeInput(time) {
            if (this.field && this.field.name.includes('cut_end') || this.field.name.includes('cut_start')) { 
                this.inputValue = time
            }
        },
        handlePageScroll() {
            document.addEventListener('wheel', function (event) {
                if (document.activeElement.type === 'number' &&
                    document.activeElement.classList.contains('noscroll')) {
                    document.activeElement.blur()
                }
            }, { passive: true })
        },
        handleUnit() {
            if (this.field.units && this.field.units !== '') {
                this.units = true
                const values = this.field.units.split(',')
                const options = {}
                values.forEach((v) => {
                    options[v.split(':')[0]] = v.split(':')[1]
                })
                this.defaultUnit = Object.keys(options)[0]
                this.options = options
            }
        },
        initializeInput() {
            if (this.field.input_type && this.numberTypes.includes(this.field.input_type)) {
                this.inputType = 'number'
            }

            // If the field is a PDF resize dimension, we will not apply input filter for validation
            // as it is handled separately in the vue component.
            if (this.isPdfResizeDimension) {
                return;
            }

            /**
             * The input is being validated against the validation regex.
             * If there min and max values and the input exceeds these 
             * limits, we will auto reset the value to nearest limit.
             */
            const max = this.field.customVariables?.max
            const min = this.field.customVariables?.min

            const is_page_range = this.field.customVariables?.is_page_range
            const self = this
            setInputFilter(
                document.getElementById(this.field.id),
                max,
                min,
                this.field.input_type,
                (this.field.input_type === 'number' || ['thousands_separator','decimal_separator'].includes(this.field.name)), 
                (value) => {
                    let isInLimit = true
                    if (is_page_range) {
                        const is_range = value.includes('-')
                        let values = is_range ? value.split('-') : value.split(',')
                        values = values.map(val =>  {
                            if(!Number.isInteger(val)) self.isValid = false
                            if((max && val > max) || (min && val < min)) {
                                self.isValid = false; 
                                isInLimit = false;
                            }
                            return val
                        })
                        // const updated_values = is_range ? values.join('-') : values.join(',')
                    }

                    if(this.field.input_type === "number") {
                        if(max && value > max) this.inputValue = max 
                        if(min && value < min) this.inputValue = min 
                    }
                    
                    if(this.inputType === 'number') {
                        isInLimit = (max ? value <= max : true) && (min ? value >= min : true)
                    }

                    if(this.field.input_type === "string" && !is_page_range) {
                        if(max && value.length > max) this.inputValue = value.substring(0,max) 
                        if(min && value.length < min) this.inputValue = value.substring(0,min) 
                        isInLimit = (max ? value.length <= max : true) && (min ? value.length >= min : true)
                    }
                                        
                    return stringToRegex(this.field.validation_regex).test(value) && isInLimit
                },
                () => self.isValid = true,
                () => self.isValid = false
            )
        },
        handleCropPreset() {
            const self = this
            this.$bus.$on('CropPresetApply', (data) => {
                setTimeout(() => {
                    if (this.field.name === 'crop-position-x') {
                        self.inputValue = data[this.$store.state.advancedSetting.cropSettingsIds['crop-position-x']]
                        self.handleKeyPress(null, data[this.$store.state.advancedSetting.cropSettingsIds['crop-position-x']])
                    }
                    setTimeout(() => {
                        if (this.field.name === 'crop-position-y') {
                            self.inputValue = data[this.$store.state.advancedSetting.cropSettingsIds['crop-position-y']]
                            self.handleKeyPress(null, data[this.$store.state.advancedSetting.cropSettingsIds['crop-position-y']])
                        }
                    }, 300)
                }, 0)
            })
        },
        onInput(val) {
            if (this.units) this.$emit('input', val + '|' + this.dataUnit)
            else this.$emit('input', val)
        },
        onSelect(e) {
            this.$emit('input', this.dataValue + '|' + e.target.value)
        },
        handleKeyPress(e) {
            e.stopPropagation()
            if (this.field.name === this.delayOptionKey) {
                setTimeout(() => {this.$bus.$emit('UPDATE_GIF_DELAY')}, 1000)
            }
            if (['crop-position-x', 'crop-position-y'].includes(this.field.name)) {
                if (e.key === '-' || e.key === 'Enter') return
                const cropper = getCropper()
                const as = Object.assign({}, JSON.parse(JSON.stringify(this.$store.state.advancedSetting.userData)));
                if (cropper) {
                    let cropData = cropper.getCropBoxData()
                    let data = cropper.getData(true)
                    if (this.field.name === 'crop-position-x') {
                        const videoWidth = document.getElementById('VideoPlayer_html5_api').videoWidth
                        const scaleX = videoWidth / document.querySelector('.cropper-canvas').clientWidth
                        const limitX = cropper.imageData.naturalWidth - data.width
                        setTimeout(() => {
                            if (this.inputValue > limitX) { this.inputValue = Number(limitX).toFixed(0) }
                            if (this.inputValue === '') { this.inputValue = 0 }
                            cropper.setCropBoxData({
                                top: cropData.top,
                                width: cropData.width,
                                height: cropData.height,
                                left: this.inputValue > limitX ? (limitX / scaleX) : (this.inputValue / scaleX)
                            })
                            const selection = as.allSelection.finalSelection['video_crop']
                            as.allSelection.finalSelection = { video_crop: `${selection.split(':')[0]}:${selection.split(':')[1]}:${this.inputValue}:${selection.split(':')[3]}` }
                            this.$store.commit("advancedSetting/setUserData", as)
                        }, 200)
                    }
                    if (this.field.name === 'crop-position-y') {
                        const videoHeight = document.getElementById('VideoPlayer_html5_api').videoHeight
                        const scaleY = videoHeight / document.querySelector('.cropper-canvas').clientHeight

                        const limitY = cropper.imageData.naturalHeight - data.height
                        setTimeout(() => {
                            if (this.inputValue > limitY) { this.inputValue = Number(limitY).toFixed(0) }
                            if (this.inputValue === '') { this.inputValue = 0 }
                            cropper.setCropBoxData({
                                top: this.inputValue > limitY ? (limitY / scaleY) : (this.inputValue / scaleY),
                                width: cropData.width,
                                height: cropData.height,
                                left: cropData.left
                            })
                            const selection = as.allSelection.finalSelection['video_crop']
                            as.allSelection.finalSelection = { video_crop: `${selection.split(':')[0]}:${selection.split(':')[1]}:${selection.split(':')[2]}:${this.inputValue}` }
                            this.$store.commit("advancedSetting/setUserData", as)
                        }, 200)
                    }
                }
            }
        }
    }
}
